import type { XrefPump } from "@/composables/forms/formTypes";
import { getSimplifiedProducedTdh, getSimplifiedRequiredBhp } from "../tornado";
import type { Point } from "@incytestudios/ddv";
import { PoundsPerCubicFoot } from "@/libs/units/density";

export const getDensityCorrectionRatio = (
  flowRate: number,
  ascendingOrderPumps: XrefPump[],
  gasHeadRerateRatio: number,
  pumpWearHeadCorrectionRatio: number,
  pumpWearBHPCorrectionRatio: number,
  dischargeFluidDensityLBMFT3: number,
  frequencyRatio: number,
  requiredTargetTDH: number,
  requiredTargetPressurePSIA: number,
  requiredTargetBrakeHP: number
): {
  densityCorrectionTDHRatio: number;
  densityCorrectionPressureRatio: number;
  densityCorrectionBHPRatio: number;
} => {
  // Implementation for density correction ratio calculation
  const simplifiedProducedTDHData = getSimplifiedProducedTdh(
    flowRate,
    frequencyRatio,
    ascendingOrderPumps,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    dischargeFluidDensityLBMFT3
  );
  const simplifiedProducedTDH = simplifiedProducedTDHData.tdh;
  const simplifiedProducedPressurePSIA = simplifiedProducedTDHData.tdhPressure;

  const densityCorrectionTDHRatio = requiredTargetTDH / simplifiedProducedTDH;
  const densityCorrectionPressureRatio =
    requiredTargetPressurePSIA / simplifiedProducedPressurePSIA;

  const fluidDensitySpGr = PoundsPerCubicFoot.toStandardUnit(
    dischargeFluidDensityLBMFT3
  );
  const simplifiedProducedBhpData = getSimplifiedRequiredBhp(
    flowRate,
    frequencyRatio,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  );
  const densityCorrectionBHPRatio =
    requiredTargetBrakeHP / simplifiedProducedBhpData.bhp;
  return {
    densityCorrectionTDHRatio,
    densityCorrectionPressureRatio,
    densityCorrectionBHPRatio
  };
};

export const getDensityCorrectedProducedPoints = <P extends Point>(
  producedPoints: P[],
  densityCorrectionRatio?: number
): P[] => {
  // Invalid ratio means no correction; return original reference for cheap no-op.
  if (
    !Number.isFinite(densityCorrectionRatio) ||
    Number(densityCorrectionRatio) <= 0
  ) {
    return producedPoints;
  }

  // Preserve x-values and uniformly scale all y-values by the computed ratio.
  return producedPoints.map(point => ({
    ...point,
    y: Number(point.y) * Number(densityCorrectionRatio)
  })) as P[];
};
